import { useCallback, useState } from 'react';

import { Typography } from '@mui/material';
import { GridPaginationModel, GridRowModel, GridSortModel } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Page } from 'common/components/Page';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { selectPaginationGridData, selectPaginationMode, setPaginationGridData, setPaginationModel } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { DISPLAYED_GRID_ROWS_COUNT } from 'common/constants/pagination';
import { PaginationMode } from 'common/constants/paginationMode';
import { Routes, RoutesNameMap } from 'common/constants/routes';
import { usePalmFetchPaginatedEntities } from 'common/hooks/usePalmFetchPaginatedEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchUsers, fetchUsersForExport } from 'common/services/api/Users/Users.thunk';
import { ActionDefinition, ColumnDefinition, ColumnType, Operations } from 'common/types/TypeDefs';

import { UserCreateContent } from './UsersCreateDrawerContent';
import { addNewFilter, clearFilters, removeFilter, selectFilters } from './usersSlice';

export function Users() {
    const [processing, setProcessing] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const paginationMode = useAppSelector(selectPaginationMode) ?? PaginationMode.Server;
    const [ defaultSortModel ] = usePalmSortModel('principalName');

    const filters = useAppSelector(selectFilters);

    const fetchGridData = usePalmFetchPaginatedEntities(
        'User',
        {},
        fetchUsers,
        defaultSortModel,
        setProcessing,
        selectFilters,
        [filters]
    );

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters
    );

    const fetchExportData = async (signal: AbortSignal, search?: string, model?: GridSortModel): Promise<GridRowModel[]> =>{
        return await dispatch(fetchUsersForExport({
            search: search ?? '',
            sortKey: model?.[0]?.field,
            sortOrder: model?.[0]?.sort,
            rowsCount: paginationGridData?.rowsCount,
            signal
        }))
            .unwrap()
            .then((response) => {
                return response;
            });
    };

    const refreshDataGrid = () => {
        fetchGridData(paginationGridData?.currentPage ?? 0, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel);
    };

    const handlePageChange = (paginationModel: GridPaginationModel) => {
        dispatch(setPaginationModel(paginationModel));
        fetchGridData(paginationModel.page, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel);
    };

    const handleSortModelChange = (model: GridSortModel) => {
        if(!paginationGridData?.sortModel?.length && !model?.length) {
            return;
        }
        dispatch(setPaginationGridData({
            ...paginationGridData,
            sortModel: model,
        }));
        fetchGridData(paginationGridData?.currentPage ?? 0, filters.keywords[0], model);
    };

    const onGetRowLink = useCallback((id: string) => {
        return `/${Routes.Users}/${id}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: false, urlToOpen: onGetRowLink },
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'userId', columnName: 'User ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'principalName', columnName: 'Name', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'userEmail', columnName: 'User Email', columnType: ColumnType.Tooltip, copy: false, sortable: false },
        { columnId: 'companyKey', columnName: 'Company Key', columnType: ColumnType.Tooltip, copy: true },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action },
    ];

    return (
        <Page
            title={RoutesNameMap.get(Routes.Users)}
            header={(
                <Typography variant="h5" gutterBottom component="div" marginTop={'1rem'}>
                    {RoutesNameMap.get(Routes.Users)}
                </Typography>
            )}
        >
            <PalmDataGrid
                title='New User'
                processing={processing}
                actionDefinitions={actionDefinitions}
                columnDefinitions={columnDefinitions}
                filter={filterProp}
                contentFunction={UserCreateContent}
                paginationMode={paginationMode}
                searchFilterDisabled={filters.keywords.length !== 0}
                paginationInfo={{
                    handlePageChange,
                    handleSortModelChange,
                    take: DISPLAYED_GRID_ROWS_COUNT,
                }}
                refreshDataGridClick={refreshDataGrid}
                getRowLink={onGetRowLink}
                sortModel={paginationGridData?.sortModel ?? defaultSortModel}
                shouldShowExportButton={true}
                fetchExportData={fetchExportData}
            />
        </Page>
    );
}
